// src/app/core/auth.service.ts
import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { buildLogoutCascadeUrlFrom } from './logout-cascade.util';

export interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
  scope?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);

  // التخزين
  private storage: Storage = sessionStorage;
  private readonly K_ACCESS = 'auth.access_token';
  private readonly K_REFRESH = 'auth.refresh_token';
  private readonly K_EXPIRES = 'auth.expires_at';
  private readonly K_REMEMBER = 'auth.remember';

  // جاهزية الخدمة
  private _ready$ = new BehaviorSubject<boolean>(false);
  readonly ready$ = this._ready$.asObservable();

  private refreshTimer: any = null;

  /** يستدعى مبكرًا من APP_INITIALIZER – يعيد Promise ينتظره الإقلاع */
  async init(): Promise<void> {
    this.loadStorageKind();

    const at = this.storage.getItem(this.K_ACCESS);
    const rt = this.storage.getItem(this.K_REFRESH);
    const exp = Number(this.storage.getItem(this.K_EXPIRES) || 0);

    try {
      if (!at && rt) {
        // جرّب Refresh عند الإقلاع
        await this.refreshToken();
      } else if (at && Date.now() < exp) {
        this.scheduleRefresh();
      } else if (at && Date.now() >= exp && rt) {
        await this.refreshToken();
      } else {
        // no tokens
        this.clearTimersOnly();
      }
    } catch {
      void this.clearLocalSessionOnly();
    } finally {
      this._ready$.next(true);
    }
  }

  /** تغيير نوع التخزين حسب remember */
  setRemember(remember: boolean) {
    const old = this.storage;
    this.storage = remember ? localStorage : sessionStorage;
    if (old !== this.storage) {
      // نقل القيم
      const a = old.getItem(this.K_ACCESS);
      const r = old.getItem(this.K_REFRESH);
      const e = old.getItem(this.K_EXPIRES);
      if (a) this.storage.setItem(this.K_ACCESS, a);
      if (r) this.storage.setItem(this.K_REFRESH, r);
      if (e) this.storage.setItem(this.K_EXPIRES, e);
      old.removeItem(this.K_ACCESS);
      old.removeItem(this.K_REFRESH);
      old.removeItem(this.K_EXPIRES);
    }
    this.storage.setItem(this.K_REMEMBER, remember ? '1' : '0');
  }

  private loadStorageKind() {
    const remember = localStorage.getItem(this.K_REMEMBER) === '1';
    this.storage = remember ? localStorage : sessionStorage;
  }

  isAuthenticated(): boolean {
    const at = this.storage.getItem(this.K_ACCESS);
    if (!at) return false;
    const exp = Number(this.storage.getItem(this.K_EXPIRES) || 0);
    return Date.now() < exp;
  }

  get accessToken(): string | null {
    // Primary store first (respects user's "remember me" choice).
    // Fallback to localStorage so a brand-new tab on this origin can pick up
    // the token stored by another tab — enables silent SSO when launching
    // external apps without forcing a full redirect+re-login.
    return (
      this.storage.getItem(this.K_ACCESS) ??
      localStorage.getItem(this.K_ACCESS)
    );
  }

  get claims(): any | null {
    const token = this.accessToken;
    if (!token) return null;
    try {
      const payload = token.split('.')[1];
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const pad =
        normalized.length % 4 === 2
          ? '=='
          : normalized.length % 4 === 3
          ? '='
          : '';
      const json = atob(normalized + pad);
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  hasPermission(perm: string): boolean {
    const c = this.claims;
    if (!c) return false;
    let perms: string[] = [];
    if (Array.isArray(c.perm)) perms = c.perm;
    else if (typeof c.perm === 'string') perms = [c.perm];

    // إزالة "perm:" prefix إذا كان موجوداً (مثل "perm:auth:*" -> "auth:*")
    let permissionToCheck = perm;
    if (perm.startsWith('perm:')) {
      permissionToCheck = perm.substring(5); // إزالة "perm:"
    }

    // SystemOwner root access: passes every permission check.
    if (perms.includes('system:*')) return true;

    if (permissionToCheck.includes(':')) {
      const parts = permissionToCheck.split(':');
      const sys = parts[0];
      const action = parts.length > 1 ? parts[1] : '*';

      // التحقق المباشر
      if (perms.includes(permissionToCheck)) return true;

      // التحقق من wildcard: إذا كان المستخدم لديه "sys:*" فيحصل على كل صلاحيات sys
      if (perms.includes(`${sys}:*`)) return true;

      // إذا طلبنا wildcard (مثل "auth:*") والصلاحية موجودة مباشرة
      if (action === '*' && perms.some(p => p.startsWith(`${sys}:`))) return true;
    }

    return perms.includes(permissionToCheck);
  }

  async loginWithPassword(
    username: string,
    password: string,
    device?: string,
    remember = false
  ): Promise<void> {
    this.setRemember(remember);

    const body = new HttpParams()
      .set('grant_type', 'password')
      .set('client_id', environment.auth.clientId)
      .set('username', username)
      .set('password', password)
      .set('scope', environment.auth.scope)
      .set('device', device || 'web');

    try {
      const tr = await firstValueFrom(
        this.http.post<TokenResponse>(environment.auth.tokenEndpoint, body.toString(), {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        })
      );

      this.storeTokens(tr);
      this.scheduleRefresh();
      this._ready$.next(true); // في حال صفحة اللوجين وحدها
    } catch (e) {
      const err = e as HttpErrorResponse;
      const code = (err.error?.error as string) || err.statusText;
      const desc = (err.error?.error_description as string) || 'Login failed.';
      if (code === 'password_change_required')
        throw new Error('يجب تغيير كلمة المرور قبل المتابعة.');
      if (code === 'session_limit_reached')
        throw new Error('تم بلوغ الحد الأقصى للجلسات المتزامنة.');
      if (code === 'invalid_grant') throw new Error('بيانات الدخول غير صحيحة.');
      throw new Error(desc);
    }
  }

  async refreshToken(): Promise<void> {
    this.loadStorageKind();
    const rt = this.storage.getItem(this.K_REFRESH);
    if (!rt) throw new Error('لا يوجد Refresh Token');

    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('client_id', environment.auth.clientId)
      .set('refresh_token', rt);

    const tr = await firstValueFrom(
      this.http.post<TokenResponse>(environment.auth.tokenEndpoint, body, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      })
    );

    this.storeTokens(tr);
    this.scheduleRefresh();
  }

  /**
   * مسح التوكنات المحلية فقط — لمسار /logout-callback في سلسلة الخروج الموحّد.
   */
  clearLocalSessionOnly(): void {
    this.clearTimersOnly();
    // Clear from both stores — primary (this.storage) AND the localStorage
    // mirror used for cross-tab SSO. Without the mirror sweep, logout would
    // leave a stale token visible to any tab that opens AuthPortal next.
    for (const store of [this.storage, localStorage] as Storage[]) {
      store.removeItem(this.K_ACCESS);
      store.removeItem(this.K_REFRESH);
      store.removeItem(this.K_EXPIRES);
    }
  }

  /**
   * خروج كامل: إبطال جلسات البوابة على الخادم، مسح كوكي المصادقة، إنهاء جلسة SPA،
   * ثم زيارة Lab وHR لمسح تخزينهما (كخروج من الثلاثة أنظمة).
   */
  async logout(): Promise<void> {
    const sessionId = this.getSessionId();
    const accessToken = this.accessToken;
    const authPortalUrl = environment.apiBase.replace(/\/+$/, '');

    try {
      if (accessToken) {
        await fetch(`${authPortalUrl}/api/sessions/me/revoke-all`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
        }).catch(() => {});
      }
      await fetch(`${authPortalUrl}/account/logout`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {});
    } catch {
      /* ignore */
    }

    if (sessionId) {
      try {
        await firstValueFrom(
          this.http.post(
            `${environment.apiBase}/api/auth/logout?sessionId=${sessionId}`,
            null
          )
        );
      } catch (error) {
        console.warn('Failed to revoke session on backend:', error);
      }
    }

    this.clearLocalSessionOnly();

    const cascade = buildLogoutCascadeUrlFrom('gateway');
    if (cascade) {
      window.location.href = cascade;
      return;
    }
    const fallbackLogin = environment.ssoLogout?.gatewayLoginUrl?.trim();
    window.location.href =
      fallbackLogin && fallbackLogin.length > 0 ? fallbackLogin : '/login';
  }

  private getSessionId(): string | null {
    const c = this.claims;
    if (!c) return null;
    return c.sid || null;
  }

  // ===== Helpers =====
  private storeTokens(tr: TokenResponse) {
    const now = Date.now();
    const expMs = (tr.expires_in ?? 3600) * 1000;
    const expAt = String(now + expMs);

    // Primary store reflects the user's remember-me choice.
    this.storage.setItem(this.K_ACCESS, tr.access_token);
    if (tr.refresh_token)
      this.storage.setItem(this.K_REFRESH, tr.refresh_token);
    this.storage.setItem(this.K_EXPIRES, expAt);

    // Mirror to localStorage so newly-opened tabs on this origin can read
    // the same token (sessionStorage is tab-scoped, which broke silent SSO
    // when launching external apps from the apps page).
    if (this.storage !== localStorage) {
      localStorage.setItem(this.K_ACCESS, tr.access_token);
      if (tr.refresh_token)
        localStorage.setItem(this.K_REFRESH, tr.refresh_token);
      localStorage.setItem(this.K_EXPIRES, expAt);
    }
  }

  private scheduleRefresh() {
    this.clearTimersOnly();
    const exp = Number(this.storage.getItem(this.K_EXPIRES) || 0);
    const now = Date.now();
    const ms = Math.max(10_000, Math.floor((exp - now) * 0.8)); // قبل الانتهاء ~20%
    this.refreshTimer = setTimeout(() => {
      this.refreshToken().catch(() => void this.clearLocalSessionOnly());
    }, ms);
  }

  private clearTimersOnly() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}
